import { useId, useState } from 'react'
import { fb } from '../../copyFeatBrowse'
import { cleanWord, MAX_WORDS, WORD_MAX_LEN } from '../../lib/words'
import { Close } from '../../ui/icons'

/** One list of words (R-94): a field, Add (or Enter), and the words as chips with a remove button each. */
function WordList({ label, placeholder, words, onChange, kind }: { label: string; placeholder: string; words: string[]; onChange: (w: string[]) => void; kind: 'include' | 'exclude' }) {
  const id = useId()
  const [text, setText] = useState('')
  const full = words.length >= MAX_WORDS
  const add = () => {
    // "jazz, pottery" adds two
    const next = [...words]
    for (const part of text.split(',')) {
      const w = cleanWord(part)
      if (w.length >= 2 && !next.includes(w) && next.length < MAX_WORDS) next.push(w)
    }
    if (next.length !== words.length) onChange(next)
    setText('')
  }
  return (
    <div data-review-id={`words-${kind}`}>
      <label htmlFor={id} className="t-control mb-1.5 block text-ink">{label}</label>
      <div className="flex gap-2">
        <input id={id} value={text} maxLength={WORD_MAX_LEN * 3} placeholder={placeholder} enterKeyHint="done" autoComplete="off" disabled={full}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => { if (cleanWord(text)) add() } /* typed and went straight to Show results: the word still counts */}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          className="t-body min-h-11 min-w-0 flex-1 rounded-[12px] bg-surface px-3.5 text-ink ring-1 ring-inset ring-boundary/60 outline-none placeholder:text-ink-2 focus:ring-2 focus:ring-focus disabled:opacity-40" />
        <button type="button" onClick={add} disabled={!cleanWord(text) || full}
          className="press t-control inline-flex min-h-11 shrink-0 items-center rounded-[12px] px-4 font-semibold text-accent-ink ring-1 ring-inset ring-boundary disabled:opacity-40">
          {fb('fb.words.add')}
        </button>
      </div>
      {full && <p className="t-caption mt-1 text-ink-2">{fb('fb.words.full')}</p>}
      {words.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {words.map((w) => (
            <li key={w} className="inline-flex min-h-11 max-w-full items-center gap-0.5 rounded-full bg-sunk pl-3.5 text-[14px] font-medium text-ink">
              <span className="min-w-0 break-all">{w}</span>
              <button type="button" aria-label={fb('fb.words.remove', { word: w })} onClick={() => onChange(words.filter((x) => x !== w))}
                className="press inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:text-ink">
                <Close size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Include and exclude words (R-94, alvin's pin 565). Part of the Filters draft: applied with Show results. */
export function WordsFilter({ inc, exc, onChange }: { inc: string[]; exc: string[]; onChange: (p: { inc?: string[]; exc?: string[] }) => void }) {
  return (
    <fieldset data-review-id="words-filter" className="min-w-0" /* a fieldset is min-content wide by default: one long word widened the sheet */>
      <legend className="t-eyebrow mb-1 text-ink-2">{fb('fb.words.legend')}</legend>
      <p className="t-caption mb-3 text-ink-2">{fb('fb.words.tip')}</p>
      <div className="space-y-4">
        <WordList kind="include" label={fb('fb.words.include')} placeholder={fb('fb.words.include_placeholder')} words={inc}
          onChange={(w) => onChange({ inc: w, exc: exc.filter((x) => !w.includes(x)) })} />
        <WordList kind="exclude" label={fb('fb.words.exclude')} placeholder={fb('fb.words.exclude_placeholder')} words={exc}
          onChange={(w) => onChange({ exc: w, inc: inc.filter((x) => !w.includes(x)) })} />
      </div>
    </fieldset>
  )
}
